import React, { useEffect, useState, useCallback, useRef } from "react";
import api from "../api";
import { useAuth } from "../contexts/AuthContext";
import { uploadAndProcessImage, uploadBackgroundBase64 } from "../imageUploadUtils";
import Alert from "../components/Alert";

// Color palette — dark theme consistent throughout
const C = {
  bg: "#1a1a2e",
  panel: "#16213e",
  panelAlt: "#0f3460",
  border: "#2a2a4a",
  text: "#e0e0e0",
  textDim: "#9a9abf",
  textMuted: "#7a7a9f",
  accent: "#4a90e2",
  green: "#27ae60",
  red: "#e74c3c",
  yellow: "#f39c12",
  white: "#ffffff",
  selectBg: "#1a1a3e",
  inputBg: "#1e1e3a",
  inputBorder: "#3a3a5a",
  hover: "#1f1f3f",
  rowEven: "#1a1a2e",
  rowOdd: "#181830",
};

const DARK_FORM_STYLE = {
  padding: 20,
  height: "100%",
  display: "flex",
  flexDirection: "column",
  background: C.panel,
  color: C.text,
  borderRadius: 0,
};

const sectionHeader = (title) => ({
  marginTop: 0,
  marginBottom: 20,
  color: C.text,
  fontSize: 16,
  fontWeight: 600,
});

const INPUT_STYLE = {
  width: "100%",
  padding: "10px 12px",
  boxSizing: "border-box",
  border: `1px solid ${C.inputBorder}`,
  borderRadius: 4,
  fontSize: 14,
  background: C.inputBg,
  color: C.text,
};

const SELECT_STYLE = {
  ...INPUT_STYLE,
  cursor: "pointer",
};

const CHECKBOX_LABEL = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  cursor: "pointer",
  color: C.text,
  fontWeight: 500,
  marginBottom: 12,
};

// ── Deck form ──

const UserDeckForm = ({ mode, deck, onSave, onCancel, languages, userDecks, symbols }) => {
  const { user } = useAuth();
  const [isPublic, setIsPublic] = useState(deck?.is_public || false);
  const [isExtension, setIsExtension] = useState(deck?.is_extension || false);
  const [mainDeckId, setMainDeckId] = useState(deck?.main_deck_id != null ? deck.main_deck_id : null);
  const [availableMainDecks, setAvailableMainDecks] = useState([]);
  const [translations, setTranslations] = useState({});
  const [deckSymbolId, setDeckSymbolId] = useState(deck?.deck_symbol_id || 0);
  const [loadingTranslations, setLoadingTranslations] = useState(false);
  const [symbolDropdownOpen, setSymbolDropdownOpen] = useState(false);
  const symbolDropdownRef = useRef(null);
  const [mainDeckDropdownOpen, setMainDeckDropdownOpen] = useState(false);
  const mainDeckDropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!symbolDropdownOpen) return;
    const handleClick = (e) => {
      if (symbolDropdownRef.current && !symbolDropdownRef.current.contains(e.target)) {
        setSymbolDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [symbolDropdownOpen]);

  // Close main deck dropdown on outside click
  useEffect(() => {
    if (!mainDeckDropdownOpen) return;
    const handleClick = (e) => {
      if (mainDeckDropdownRef.current && !mainDeckDropdownRef.current.contains(e.target)) {
        setMainDeckDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [mainDeckDropdownOpen]);

  useEffect(() => {
    const userNonExt = (userDecks || [])
      .filter(d => !d.is_extension && d.deck_id !== deck?.deck_id)
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .map(d => ({ ...d, isUserDeck: true }));
    setAvailableMainDecks(userNonExt);

    if (mode === "edit" && deck) {
      setIsPublic(!!deck.is_public);
      setIsExtension(!!deck.is_extension);
      setDeckSymbolId(deck.deck_symbol_id || 0);
      setLoadingTranslations(true);
      Promise.all([
        api.get(`/api/decks/${deck.deck_id}/translations`).catch(() => ({ data: [] })),
      ])
        .then(([tRes]) => {
          const map = {};
          (tRes.data || []).forEach(t => { map[t.language] = { name: t.name }; });
          setTranslations(map);
        })
        .catch(() => {})
        .finally(() => setLoadingTranslations(false));
    } else {
      // Add mode: initialize translations from languages list
      const init = {};
      (languages || []).forEach(l => { init[l.code] = { name: "" }; });
      setTranslations(init);
    }
  }, [mode, deck, userDecks, languages]);

  const handleTranslationChange = (lang, field, value) => {
    setTranslations(prev => ({
      ...prev,
      [lang]: { ...prev[lang], [field]: value },
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const payload = { isPublic, isExtension, deck_symbol_id: deckSymbolId };
    if (isExtension && mainDeckId) payload.main_deck_id = mainDeckId;
    payload.translations = translations;
    onSave(payload);
  };

  if (loadingTranslations) return <div style={{ padding: 20, color: C.textDim }}>Loading...</div>;

  const symbolOptions = (symbols || []).filter(s => s.symbol_type === "Deck" || !s.symbol_type);

  return (
    <form onSubmit={handleSubmit} style={DARK_FORM_STYLE}>
      <div style={sectionHeader("Deck name")}>
        <div style={{ display: "grid", gap: 4 }}>
          {languages.map(lang => (
            <div key={lang.code} style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 80, fontSize: 13, color: C.textDim, flexShrink: 0 }}>{lang.name}</span>
              <input
                type="text"
                placeholder={`Name in ${lang.name}`}
                value={translations[lang.code]?.name || ""}
                onChange={e => handleTranslationChange(lang.code, "name", e.target.value)}
                style={INPUT_STYLE}
              />
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 15, padding: 12, background: C.panelAlt, borderRadius: 4, border: `1px solid ${C.border}` }}>
        <label style={CHECKBOX_LABEL}>
          <input type="checkbox" checked={isExtension} onChange={e => setIsExtension(e.target.checked)} style={{ width: 18, height: 18, cursor: "pointer" }} />
          <span style={{ fontWeight: 500 }}>Extension Deck</span>
        </label>
      </div>

      {isExtension && (
        <div style={{ marginBottom: 15 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600, color: C.text }}>
            Main Deck <span style={{ color: C.red }}>*</span>
          </label>
          <div style={{ position: "relative", marginBottom: 4 }} ref={mainDeckDropdownRef}>
            <div style={{ ...SELECT_STYLE, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", width: "100%", background: mainDeckId ? C.panelAlt : C.inputBg }}
              onClick={() => setMainDeckDropdownOpen(!mainDeckDropdownOpen)}>
              {mainDeckId > 0 && (availableMainDecks.find(d => d.deck_id === mainDeckId)) ? (
                <img src={(availableMainDecks.find(d => d.deck_id === mainDeckId))?.deck_symbol_path || "/uploads/images/symbols/default.png"}
                  alt="" style={{ width: 28, height: 28, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 3 }}
                  onError={e => e.target.src = "/uploads/images/symbols/default.png"} />
              ) : (
                <span style={{ fontSize: 16, marginRight: 4 }}>⬜</span>
              )}
              <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13, color: C.text }}>
                {mainDeckId > 0
                  ? (availableMainDecks.find(d => d.deck_id === mainDeckId)?.name || `Deck #${mainDeckId}`)
                  : (isExtension ? "-- Select Main Deck --" : "—")}
              </span>
              <span style={{ color: C.textDim, fontSize: 12, paddingLeft: 4 }}>▼</span>
             </div>
            {mainDeckDropdownOpen && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4, maxHeight: 320, overflowY: "auto", padding: 4, boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}>
                {availableMainDecks.length === 0 ? (
                  <div style={{ padding: "8px 12px", color: C.textMuted, fontSize: 13 }}>No decks available</div>
                ) : (
                  [...availableMainDecks].sort((a, b) => (a.name || `Deck ${a.deck_id}`).localeCompare(b.name || `Deck ${b.deck_id}`)).map(d => (
                    <div key={d.deck_id} onClick={() => { setMainDeckId(d.deck_id); setMainDeckDropdownOpen(false); }}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", cursor: "pointer", borderRadius: 4 }}>
                      <img src={d.deck_symbol_path || "/uploads/images/symbols/default.png"}
                        alt="" style={{ width: 24, height: 24, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 3 }}
                        onError={e => e.target.src = "/uploads/images/symbols/default.png"} />
                      <span style={{ flex: 1, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {d.name}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div style={{ marginTop: 15, padding: 12, background: C.panelAlt, borderRadius: 4, border: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <span style={{ fontWeight: 600, color: C.text }}>Deck Symbol</span>
        </div>
        <div style={{ position: "relative", marginBottom: 4 }}>
          <div style={{ ...SELECT_STYLE, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", width: "100%", background: deckSymbolId > 0 ? C.panelAlt : C.inputBg }} onClick={() => setSymbolDropdownOpen(!symbolDropdownOpen)}>
            {deckSymbolId > 0 && (symbolOptions.find(s => s.symbol_id === deckSymbolId)) ? (
              <img src={symbolOptions.find(s => s.symbol_id === deckSymbolId)?.symbol}
                alt="" style={{ width: 28, height: 28, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 3 }} />
            ) : (
              <span style={{ fontSize: 16, marginRight: 4 }}>⬜</span>
            )}
            <span style={{ flex: 1, textAlign: "left", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 13, color: C.text }}>
              {deckSymbolId > 0
                ? (symbolOptions.find(s => s.symbol_id === deckSymbolId)?.symbol_name || `Symbol #${deckSymbolId}`)
                : "-- No symbol --"}
            </span>
            <span style={{ color: C.textDim, fontSize: 12, paddingLeft: 4 }}>▼</span>
          </div>
          {symbolDropdownOpen && (
            <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 100, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4, maxHeight: 320, overflowY: "auto", padding: 4, boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }} ref={symbolDropdownRef}>
              {[...symbolOptions].sort((a, b) => (a.symbol_name || a.symbol_id).localeCompare(b.symbol_name || b.symbol_id)).map(s => (
                <div key={s.symbol_id} onClick={() => { setDeckSymbolId(s.symbol_id); setSymbolDropdownOpen(false); }}
                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", cursor: "pointer", borderRadius: 4 }}>
                  <img src={s.symbol}
                    alt="" style={{ width: 24, height: 24, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 3 }} />
                  <span style={{ flex: 1, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {s.symbol_name || `Symbol #${s.symbol_id}`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 15, padding: 12, background: C.panelAlt, borderRadius: 4, border: `1px solid ${C.border}` }}>
        <label style={CHECKBOX_LABEL}>
          <input type="checkbox" checked={isPublic} onChange={e => setIsPublic(e.target.checked)} style={{ width: 18, height: 18, cursor: "pointer" }} />
          <span style={{ fontWeight: 500 }}>Public Deck</span>
        </label>
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: "auto", paddingTop: 20, borderTop: `1px solid ${C.border}` }}>
        <button type="submit" style={{ flex: 1, padding: "12px 16px", fontSize: 14, fontWeight: 600, background: C.yellow, color: "#1a1a1a", border: "none", borderRadius: 4, cursor: "pointer" }}>
          {mode === "add" ? "Add Deck" : "Save Changes"}
        </button>
        <button type="button" onClick={onCancel} style={{ flex: 1, padding: "12px 16px", fontSize: 14, fontWeight: 600, background: C.inputBorder, color: C.text, border: "none", borderRadius: 4, cursor: "pointer" }}>
          Cancel
        </button>
      </div>
    </form>
  );
};

// ── Local Card Form components (with symbol icon in type dropdown + fixed symbol path) ──

function AddCardFormLocal({ decks, cardTypes, onAdd, onCancel, languages = [], symbols = [], backgroundUploadHandler, preselectedDeckId: preselectedDeckIdProp }) {
  const [deckId, setDeckId] = useState(preselectedDeckIdProp || "");
  const [typeId, setTypeId] = useState("");
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const typeDropdownRef = useRef(null);
  const [customBackground, setCustomBackground] = useState("");
  const [texts, setTexts] = useState([{ option_index: 1, translations: {} }]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const userLang = user?.language || "en";
  const [symbolDropdowns, setSymbolDropdowns] = useState({});
  const toggleSymbolDropdown = (key) => {
    setSymbolDropdowns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    if (!typeDropdownOpen) return;
    const handleClick = (e) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
        setTypeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [typeDropdownOpen]);

  const handleAddText = () => {
    setTexts([...texts, { option_index: texts.length + 1, translations: {} }]);
  };
  const handleTextChange = (textIdx, langCode, value) => {
    setTexts(texts.map((t, i) =>
      i === textIdx ? { ...t, translations: { ...t.translations, [langCode]: value } } : t
    ));
  };
  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      let fileUrl;
      if (backgroundUploadHandler) {
        fileUrl = await backgroundUploadHandler(file);
      }
      setCustomBackground(fileUrl);
    } catch (err) {
      setError(err?.message || "Failed to upload image");
    }
    setUploading(false);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const effectiveDeckId = preselectedDeckId || deckId;
    if (!effectiveDeckId || !typeId) {
      setError("Deck and type are required");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const flatTexts = [];
      texts.forEach((t, idx) => {
        Object.entries(t.translations).forEach(([lang, text]) => {
          if (text && text.trim()) {
            flatTexts.push({ option_index: idx + 1, language: lang, text: text.trim() });
          }
        });
      });
      await onAdd({
        deckId: parseInt(effectiveDeckId),
        typeId: parseInt(typeId),
        customBackground: customBackground,
        created_by: user?.user_id,
        texts: flatTexts,
      });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to add card");
    }
    setUploading(false);
  };
  const insertSymbolReference = (textIdx, langCode, symbolId) => {
    const inputId = `addcard-text-input-${textIdx}-${langCode}`;
    const input = document.getElementById(inputId);
    if (!input) return;
    const value = input.value;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const ref = `{{symbol:${symbolId}}}`;
    const newValue = value.slice(0, start) + ref + value.slice(end);
    handleTextChange(textIdx, langCode, newValue);
    setTimeout(() => {
      input.focus();
      input.selectionStart = input.selectionEnd = start + ref.length;
    }, 0);
  };
  const cardSymbols = (symbols || []).filter(s => s.symbol_type === 'Card type' || s.symbol_type === 'Symbol');

  // Card type with symbol — find the type's own symbol_filename
  const cardTypeSymbols = (symbols || []).filter(s => s.symbol_type === 'Card type');
  const symbolToTypeId = {};
  (cardTypes || []).forEach(t => { if (t.symbol_id) symbolToTypeId[t.symbol_id] = t.type_id; });
  const selectedType = typeId ? cardTypes.find(t => String(t.type_id) === String(typeId)) : null;
  const selectedTypeSymbol = typeId ? cardTypeSymbols.find(s => symbolToTypeId[s.symbol_id] === Number(typeId)) : null;
  const selectedTypeIcon = selectedTypeSymbol ? selectedTypeSymbol.symbol : (selectedType?.symbol_filename || null);

  const preselectedDeckId = preselectedDeckIdProp || decks[0]?.deck_id || "";
  const showDeckSelector = !preselectedDeckId || decks.length > 1;
  const [deckDropdownOpen, setDeckDropdownOpen] = useState(false);
  const deckDropdownRef = useRef(null);

  return (
    <div style={{ width: '100%', height: "100%", display: "flex", flexDirection: "column" }}>
      <form onSubmit={handleSubmit} style={{ padding: 20, height: "100%", display: "flex", flexDirection: "column", background: C.panel, color: C.text, borderRadius: 0 }}>
          <h3 style={{ flexShrink: 0, marginTop: 0, marginBottom: 16 }}>Add Card</h3>
          <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        {showDeckSelector && (
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontWeight: 600, color: C.text, display: "block", marginBottom: 4 }}>Deck</label>
            <div style={{ position: "relative" }} ref={deckDropdownRef}>
              <div style={{ ...SELECT_STYLE, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", maxWidth: 320, background: deckId ? C.panelAlt : C.inputBg }}
                onClick={() => setDeckDropdownOpen(!deckDropdownOpen)}>
                {(() => {
                  const sel = decks.find(d => String(d.deck_id) === String(deckId));
                  const iconPath = sel?.deck_symbol_path;
                  return iconPath ? (
                    <img src={iconPath} alt="" style={{ width: 28, height: 28, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 3 }} />
                  ) : (
                    <span style={{ fontSize: 18, width: 28, textAlign: "center" }}>📦</span>
                  );
                })()}
                <span style={{ flex: 1, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {decks.find(d => String(d.deck_id) === String(deckId))?.name ||
                   `Deck ${deckId}` || "Select deck..."}
                </span>
              </div>
              {deckDropdownOpen && (
                <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 100, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4, maxHeight: 320, overflowY: "auto", padding: 4, boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}>
                  {decks.sort((a, b) => (a.name || `Deck ${a.deck_id}`).localeCompare(b.name || `Deck ${b.deck_id}`)).map(deck => {
                    const iconPath = deck.deck_symbol_path;
                    return (
                      <div key={deck.deck_id} onClick={() => { setDeckId(deck.deck_id); setDeckDropdownOpen(false); }}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", cursor: "pointer", borderRadius: 4 }}>
                        {iconPath ? (
                          <img src={iconPath} alt="" style={{ width: 24, height: 24, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 3 }} />
                        ) : (
                          <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>📦</span>
                        )}
                        <span style={{ flex: 1, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {deck.name || `Deck ${deck.deck_id}`}
                        </span>
              </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontWeight: 600, color: C.text, display: "block", marginBottom: 4 }}>Card Type</label>
          <div style={{ position: "relative" }} ref={typeDropdownRef}>
            <div style={{ ...SELECT_STYLE, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", maxWidth: 320, background: typeId ? C.panelAlt : C.inputBg }}
              onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}>
              {selectedTypeIcon ? (
                <img src={selectedTypeIcon} alt="" style={{ width: 28, height: 28, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 3 }}
                  onError={e => e.target.src = "/uploads/images/symbols/default.png"} />
              ) : (
                <span style={{ fontSize: 18, width: 28, textAlign: "center" }}>⬜</span>
              )}
              <span style={{ flex: 1, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {selectedTypeSymbol ? selectedTypeSymbol.symbol_name || `Type ${typeId}` : "Select Type"}
              </span>
            </div>
            {typeDropdownOpen && (
              <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 100, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4, maxHeight: 320, overflowY: "auto", padding: 4, boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}>
                {cardTypeSymbols.sort((a, b) => (a.symbol_name || a.symbol_id).localeCompare(b.symbol_name || b.symbol_id)).map(symbol => {
                  const typeIdValue = symbolToTypeId[symbol.symbol_id];
                  const iconPath = symbol.symbol || null;
                  return (
                    <div key={symbol.symbol_id} onClick={() => { setTypeId(typeIdValue); setTypeDropdownOpen(false); }}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", cursor: "pointer", borderRadius: 4 }}>
                      {iconPath ? (
                        <img src={iconPath} alt="" style={{ width: 24, height: 24, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 3 }}
                          onError={e => e.target.src = "/uploads/images/symbols/default.png"} />
                      ) : (
                        <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>⬜</span>
                      )}
                      <span style={{ flex: 1, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {symbol.symbol_name || `Type ${typeIdValue}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div style={{ marginBottom: 10, padding: 12, background: C.panelAlt, borderRadius: 4, border: `1px solid ${C.border}` }}>
          <span style={{ fontWeight: 600, color: C.text, display: "block", marginBottom: 4 }}>Custom Background (any format → PNG 488×488)</span>
          <input type="file" accept="image/*" onChange={handleBackgroundUpload} style={{ width: "100%", display: "block" }} />
          {customBackground && <img src={customBackground} alt="bg" style={{ width: 80, height: 80, objectFit: 'cover', marginTop: 8, borderRadius: 4, border: `1px solid ${C.border}` }} />}
        </div>

        <div style={{ marginBottom: 8, fontWeight: 500, color: C.textDim, padding: 8, background: C.panelAlt, borderRadius: 4, border: `1px solid ${C.border}` }}>
          You can insert a symbol reference into any text option using the dropdown next to the input. The symbol will appear as <code>{'{{symbol:ID}}'}</code> in the text.
        </div>
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontWeight: 600, color: C.text, display: "block", marginBottom: 6 }}>Texts</span>
          {texts.map((t, tIdx) => (
            <div key={tIdx} style={{ marginBottom: 10, padding: 12, background: C.panelAlt, borderRadius: 4, border: `1px solid ${C.border}` }}>
              <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13, color: C.text, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span>Option {tIdx + 1}</span>
                {texts.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setTexts(texts.filter((_, i) => i !== tIdx))}
                    style={{ fontSize: 11, padding: "2px 8px", background: C.red, color: "#fff", border: "none", borderRadius: 3, cursor: "pointer" }}
                  >
                    Delete
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {languages.map(lang => (
                  <div key={lang.code} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 80, fontSize: 12, color: C.textDim, flexShrink: 0 }}>{lang.name}</span>
                    <input
                      type="text"
                      id={`addcard-text-input-${tIdx}-${lang.code}`}
                      value={t.translations?.[lang.code] || ''}
                      onChange={e => handleTextChange(tIdx, lang.code, e.target.value)}
                      placeholder={`Text in ${lang.name}`}
                      style={{ flex: 1, padding: '8px 10px', boxSizing: 'border-box', border: `1px solid ${C.inputBorder}`, borderRadius: 4, fontSize: 13, background: C.inputBg, color: C.text }}
                    />
                    {cardSymbols.length > 0 && (
                      <div style={{ position: "relative" }}>
                        <div style={{ ...SELECT_STYLE, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", minWidth: 140, background: C.inputBg }}
                          onClick={() => toggleSymbolDropdown(`${tIdx}-${lang.code}`)}>
                          {symbolDropdowns[`${tIdx}-${lang.code}`] ? "▲" : "▼"}
                          <span style={{ flex: 1, fontSize: 12, color: C.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {symbolDropdowns[`${tIdx}-${lang.code}`] ? "Select symbol..." : "Insert symbol"}
                          </span>
                        </div>
                        {symbolDropdowns[`${tIdx}-${lang.code}`] && (
                          <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 100, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4, maxHeight: 240, overflowY: "auto", padding: 4, boxShadow: "0 4px 16px rgba(0,0,0,0.4)", minWidth: 160 }}>
                            {cardSymbols.sort((a, b) => (a.symbol_name || a.symbol_id).localeCompare(b.symbol_name || b.symbol_id)).map(symbol => {
                              // symbol.symbol is already the full path — use it directly, no prefix doubling
                              const symIcon = symbol.symbol || null;
                              const symName = symbol.symbol_name || `Symbol ${symbol.symbol_id}`;
                              return (
                                <div key={symbol.symbol_id} onClick={() => { insertSymbolReference(tIdx, lang.code, symbol.symbol_id); toggleSymbolDropdown(`${tIdx}-${lang.code}`); }}
                                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", cursor: "pointer", borderRadius: 4 }}>
                                  {symIcon ? (
                                    <img src={symIcon} alt="" style={{ width: 24, height: 24, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 3 }}
                                      onError={e => e.target.src = "/uploads/images/symbols/default.png"} />
                                  ) : (
                                    <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>⬤</span>
                                  )}
                                  <span style={{ flex: 1, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {symName}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
              </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button type="button" onClick={handleAddText} style={{ marginTop: 4, fontSize: 13, background: C.accent, color: "#fff", border: "none", borderRadius: 4, padding: "6px 12px", cursor: "pointer" }}>
            + Add Option
          </button>
        </div>
        {error && <div style={{ color: C.red, padding: 8, background: C.panelAlt, borderRadius: 4, border: `1px solid ${C.border}` }}>{error}</div>}
        <div style={{ flexShrink: 0, display: "flex", gap: 10, marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          <button type="submit" disabled={uploading} style={{ flex: 1, padding: "12px 16px", fontSize: 14, fontWeight: 600, background: C.accent, color: "#fff", border: "none", borderRadius: 4, cursor: uploading ? "not-allowed" : "pointer" }}>
            {uploading ? "Saving..." : "Add Card"}
          </button>
          <button type="button" onClick={onCancel} style={{ flex: 1, padding: "12px 16px", fontSize: 14, fontWeight: 600, background: C.inputBorder, color: C.text, border: "none", borderRadius: 4, cursor: "pointer" }}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  </div>
);
  }

function EditCardFormLocal({ card, decks, cardTypes, onSave, onCancel, languages = [], symbols = [], backgroundUploadHandler, preselectedDeckId: preselectedDeckIdProp }) {
  const [deckId, setDeckId] = useState(preselectedDeckIdProp || card.deck_id || "");
  const [typeId, setTypeId] = useState(card.type_id);
  const [typeDropdownOpen, setTypeDropdownOpen] = useState(false);
  const typeDropdownRef = useRef(null);
  const [customBackground, setCustomBackground] = useState(card.custom_background || "");
  const [texts, setTexts] = useState(card.texts && card.texts.length ? card.texts : [{ option_index: 1, translations: {} }]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const { user } = useAuth();
  const userLang = user?.language || "en";
  const [symbolDropdowns, setSymbolDropdowns] = useState({});
  const toggleSymbolDropdown = (key) => {
    setSymbolDropdowns(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    if (!typeDropdownOpen) return;
    const handleClick = (e) => {
      if (typeDropdownRef.current && !typeDropdownRef.current.contains(e.target)) {
        setTypeDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [typeDropdownOpen]);

  useEffect(() => {
    if (card.texts && card.texts.length) {
      setTexts(card.texts);
    }
  }, [card.texts]);

  const handleTextChange = (textIdx, langCode, value) => {
    setTexts(texts.map((t, i) =>
      i === textIdx ? { ...t, translations: { ...t.translations, [langCode]: value } } : t
    ));
  };
  const handleAddText = () => {
    setTexts([...texts, { option_index: texts.length + 1, translations: {} }]);
  };
  const handleBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      let fileUrl;
      if (backgroundUploadHandler) {
        fileUrl = await backgroundUploadHandler(file);
      }
      setCustomBackground(fileUrl);
    } catch (err) {
      setError(err?.message || "Failed to upload image");
    }
    setUploading(false);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const effectiveDeckId = preselectedDeckId || deckId;
    if (!effectiveDeckId || !typeId) {
      setError("Deck and type are required");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const flatTexts = [];
      texts.forEach((t, idx) => {
        Object.entries(t.translations).forEach(([lang, text]) => {
          if (text && text.trim()) {
            flatTexts.push({ option_index: idx + 1, language: lang, text: text.trim() });
          }
        });
      });
      await onSave({
              deckId: parseInt(effectiveDeckId),
              typeId: parseInt(typeId),
              customBackground: customBackground,
              created_by: user?.user_id,
              texts: flatTexts,
            });
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Failed to update card");
    }
    setUploading(false);
  };
  const insertSymbolReference = (textIdx, langCode, symbolId) => {
    const inputId = `editcard-text-input-${textIdx}-${langCode}`;
    const input = document.getElementById(inputId);
    if (!input) return;
    const value = input.value;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const ref = `{{symbol:${symbolId}}}`;
    const newValue = value.slice(0, start) + ref + value.slice(end);
    handleTextChange(textIdx, langCode, newValue);
    setTimeout(() => {
      input.focus();
      input.selectionStart = input.selectionEnd = start + ref.length;
    }, 0);
  };
  const cardSymbols = (symbols || []).filter(s => s.symbol_type === 'Card type' || s.symbol_type === 'Symbol');
  const cardTypeSymbols = (symbols || []).filter(s => s.symbol_type === 'Card type');
  const symbolToTypeId = {};
  (cardTypes || []).forEach(t => { if (t.symbol_id) symbolToTypeId[t.symbol_id] = t.type_id; });
  const selectedType = typeId ? cardTypes.find(t => String(t.type_id) === String(typeId)) : null;
  const selectedTypeSymbol = typeId ? cardTypeSymbols.find(s => symbolToTypeId[s.symbol_id] === Number(typeId)) : null;
  const selectedTypeIcon = selectedTypeSymbol ? selectedTypeSymbol.symbol : (selectedType?.symbol_filename || null);
  const preselectedDeckId = preselectedDeckIdProp || card.deck_id || "";
  const showDeckSelector = !preselectedDeckId || decks.length > 1;
  const [deckDropdownOpen, setDeckDropdownOpen] = useState(false);
  const deckDropdownRef = useRef(null);

  return (
    <div style={{ width: '100%', height: "100%", display: "flex", flexDirection: "column" }}>
      <form onSubmit={handleSubmit} style={{ padding: 20, height: "100%", display: "flex", flexDirection: "column", background: C.panel, color: C.text, borderRadius: 0 }}>
        <h3 style={{ flexShrink: 0, marginTop: 0, marginBottom: 16 }}>Edit Card</h3>
        <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        {showDeckSelector && (
          <div style={{ marginBottom: 10 }}>
            <label style={{ fontWeight: 600, color: C.text, display: "block", marginBottom: 4 }}>Deck</label>
            <div style={{ position: "relative" }} ref={deckDropdownRef}>
              <div style={{ ...SELECT_STYLE, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", maxWidth: 320, background: deckId ? C.panelAlt : C.inputBg }}
                onClick={() => setDeckDropdownOpen(!deckDropdownOpen)}>
                {(() => {
                  const sel = decks.find(d => String(d.deck_id) === String(deckId));
                  const iconPath = sel?.deck_symbol_path;
                  return iconPath ? (
                    <img src={iconPath} alt="" style={{ width: 28, height: 28, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 3 }} />
                  ) : (
                    <span style={{ fontSize: 18, width: 28, textAlign: "center" }}>📦</span>
                  );
                })()}
                <span style={{ flex: 1, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {decks.find(d => String(d.deck_id) === String(deckId))?.name ||
                   `Deck ${deckId}` || "Select deck..."}
                </span>
              </div>
              {deckDropdownOpen && (
                <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 100, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4, maxHeight: 320, overflowY: "auto", padding: 4, boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}>
                  {decks.sort((a, b) => (a.name || `Deck ${a.deck_id}`).localeCompare(b.name || `Deck ${b.deck_id}`)).map(deck => {
                    const iconPath = deck.deck_symbol_path;
                    return (
                      <div key={deck.deck_id} onClick={() => { setDeckId(deck.deck_id); setDeckDropdownOpen(false); }}
                        style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", cursor: "pointer", borderRadius: 4 }}>
                        {iconPath ? (
                          <img src={iconPath} alt="" style={{ width: 24, height: 24, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 3 }} />
                        ) : (
                          <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>📦</span>
                        )}
                        <span style={{ flex: 1, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {deck.name || `Deck ${deck.deck_id}`}
                        </span>
              </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontWeight: 600, color: C.text, display: "block", marginBottom: 4 }}>Card Type</label>
          <div style={{ position: "relative" }} ref={typeDropdownRef}>
            <div style={{ ...SELECT_STYLE, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", maxWidth: 320, background: typeId ? C.panelAlt : C.inputBg }}
              onClick={() => setTypeDropdownOpen(!typeDropdownOpen)}>
              {selectedTypeIcon ? (
                <img src={selectedTypeIcon} alt="" style={{ width: 28, height: 28, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 3 }}
                  onError={e => e.target.src = "/uploads/images/symbols/default.png"} />
              ) : (
                <span style={{ fontSize: 18, width: 28, textAlign: "center" }}>⬜</span>
              )}
              <span style={{ flex: 1, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {selectedTypeSymbol ? selectedTypeSymbol.symbol_name || `Type ${typeId}` : "Select Type"}
              </span>
            </div>
            {typeDropdownOpen && (
              <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 100, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4, maxHeight: 320, overflowY: "auto", padding: 4, boxShadow: "0 4px 16px rgba(0,0,0,0.4)" }}>
                {cardTypeSymbols.sort((a, b) => (a.symbol_name || a.symbol_id).localeCompare(b.symbol_name || b.symbol_id)).map(symbol => {
                  const typeIdValue = symbolToTypeId[symbol.symbol_id];
                  const iconPath = symbol.symbol || null;
                  return (
                    <div key={symbol.symbol_id} onClick={() => { setTypeId(typeIdValue); setTypeDropdownOpen(false); }}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", cursor: "pointer", borderRadius: 4 }}>
                      {iconPath ? (
                        <img src={iconPath} alt="" style={{ width: 24, height: 24, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 3 }}
                          onError={e => e.target.src = "/uploads/images/symbols/default.png"} />
                      ) : (
                        <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>⬜</span>
                      )}
                      <span style={{ flex: 1, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {symbol.symbol_name || `Type ${typeIdValue}`}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div style={{ marginBottom: 10, padding: 12, background: C.panelAlt, borderRadius: 4, border: `1px solid ${C.border}` }}>
          <span style={{ fontWeight: 600, color: C.text, display: "block", marginBottom: 4 }}>Custom Background (any format → PNG 488×488)</span>
          <input type="file" accept="image/*" onChange={handleBackgroundUpload} style={{ width: "100%", display: "block" }} />
          {customBackground && <img src={customBackground} alt="bg" style={{ width: 80, height: 80, objectFit: 'cover', marginTop: 8, borderRadius: 4, border: `1px solid ${C.border}` }} />}
        </div>

        <div style={{ marginBottom: 8, fontWeight: 500, color: C.textDim, padding: 8, background: C.panelAlt, borderRadius: 4, border: `1px solid ${C.border}` }}>
          You can insert a symbol reference into any text option using the dropdown next to the input. The symbol will appear as <code>{'{{symbol:ID}}'}</code> in the text.
        </div>
        <div style={{ marginBottom: 10 }}>
          <span style={{ fontWeight: 600, color: C.text, display: "block", marginBottom: 6 }}>Texts</span>
          {texts.map((t, tIdx) => (
            <div key={tIdx} style={{ marginBottom: 10, padding: 12, background: C.panelAlt, borderRadius: 4, border: `1px solid ${C.border}` }}>
              <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 13, color: C.text, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span>Option {tIdx + 1}</span>
                {texts.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setTexts(texts.filter((_, i) => i !== tIdx))}
                    style={{ fontSize: 11, padding: "2px 8px", background: C.red, color: "#fff", border: "none", borderRadius: 3, cursor: "pointer" }}
                  >
                    Delete
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {languages.map(lang => (
                  <div key={lang.code} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 80, fontSize: 12, color: C.textDim, flexShrink: 0 }}>{lang.name}</span>
                    <input
                      type="text"
                      id={`editcard-text-input-${tIdx}-${lang.code}`}
                      value={t.translations?.[lang.code] || ''}
                      onChange={e => handleTextChange(tIdx, lang.code, e.target.value)}
                      placeholder={`Text in ${lang.name}`}
                      style={{ flex: 1, padding: '8px 10px', boxSizing: 'border-box', border: `1px solid ${C.inputBorder}`, borderRadius: 4, fontSize: 13, background: C.inputBg, color: C.text }}
                    />
                    {cardSymbols.length > 0 && (
                      <div style={{ position: "relative" }}>
                        <div style={{ ...SELECT_STYLE, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, padding: "6px 8px", minWidth: 140, background: C.inputBg }}
                          onClick={() => toggleSymbolDropdown(`${tIdx}-${lang.code}`)}>
                          {symbolDropdowns[`${tIdx}-${lang.code}`] ? "▲" : "▼"}
                          <span style={{ flex: 1, fontSize: 12, color: C.textDim, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {symbolDropdowns[`${tIdx}-${lang.code}`] ? "Select symbol..." : "Insert symbol"}
                          </span>
                        </div>
                        {symbolDropdowns[`${tIdx}-${lang.code}`] && (
                          <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 100, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 4, maxHeight: 240, overflowY: "auto", padding: 4, boxShadow: "0 4px 16px rgba(0,0,0,0.4)", minWidth: 160 }}>
                            {cardSymbols.sort((a, b) => (a.symbol_name || a.symbol_id).localeCompare(b.symbol_name || b.symbol_id)).map(symbol => {
                              // symbol.symbol is already the full path — use directly
                              const symIcon = symbol.symbol || null;
                              const symName = symbol.symbol_name || `Symbol ${symbol.symbol_id}`;
                              return (
                                <div key={symbol.symbol_id} onClick={() => { insertSymbolReference(tIdx, lang.code, symbol.symbol_id); toggleSymbolDropdown(`${tIdx}-${lang.code}`); }}
                                  style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", cursor: "pointer", borderRadius: 4 }}>
                                  {symIcon ? (
                                    <img src={symIcon} alt="" style={{ width: 24, height: 24, objectFit: "contain", flexShrink: 0, border: `1px solid ${C.border}`, borderRadius: 3 }}
                                      onError={e => e.target.src = "/uploads/images/symbols/default.png"} />
                                  ) : (
                                    <span style={{ fontSize: 16, width: 24, textAlign: "center" }}>⬤</span>
                                  )}
                                  <span style={{ flex: 1, fontSize: 13, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                    {symName}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
              </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button type="button" onClick={handleAddText} style={{ marginTop: 4, fontSize: 13, background: C.accent, color: "#fff", border: "none", borderRadius: 4, padding: "6px 12px", cursor: "pointer" }}>
            + Add Option
          </button>
        {error && <div style={{ color: C.red, padding: 8, background: C.panelAlt, borderRadius: 4, border: `0px solid ${C.border}` }}>{error}</div>}
        <div style={{ flexShrink: 0, display: "flex", gap: 10, marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          <button type="submit" disabled={uploading} style={{ flex: 1, padding: "12px 16px", fontSize: 14, fontWeight: 600, background: C.accent, color: "#fff", border: "none", borderRadius: 4, cursor: uploading ? "not-allowed" : "pointer" }}>
            {uploading ? "Saving..." : "Save Card"}
          </button>
          <button type="button" onClick={onCancel} style={{ flex: 1, padding: "12px 16px", fontSize: 14, fontWeight: 600, background: C.inputBorder, color: C.text, border: "none", borderRadius: 4, cursor: "pointer" }}>
            Cancel
          </button>
        </div>
        </div>
      </div>
    </form>
  </div>
);
  }

// ── Main page ──

const MyDecks = () => {
  const { user, loading: authLoading } = useAuth();
  const [decks, setDecks] = useState([]);
  const [selectedDeck, setSelectedDeck] = useState(null);
  const [deckCards, setDeckCards] = useState([]);
  const [cardTypes, setCardTypes] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [formMode, setFormMode] = useState(null);
  const [editDeckData, setEditDeckData] = useState(null);
  const [editCardData, setEditCardData] = useState(null);
  const isDeckForm = formMode && formMode.startsWith("deck");
  const isCardForm = formMode && formMode.startsWith("card");
  const deckFormMode = isDeckForm ? (formMode === "deck-edit" ? "edit" : "add") : null;
  const cardFormMode = isCardForm ? (formMode === "card-edit" ? "edit" : "add") : null;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deckSearch, setDeckSearch] = useState("");
  const [cardSearch, setCardSearch] = useState("");

  const userLang = user?.language || "en";

  useEffect(() => {
    if (!user) return;
    const fetchAll = async () => {
      try {
        const [dRes, tRes, tEnRes, sRes, enSRes, lRes] = await Promise.all([
          api.get(`/api/decks/user?lang=${encodeURIComponent(userLang)}`),
          api.get(`/api/cards/types?lang=${encodeURIComponent(userLang)}`),
          api.get(`/api/cards/types?lang=en`),
          api.get(`/api/symbols?lang=${encodeURIComponent(userLang)}`),
          api.get(`/api/symbols?lang=en`),
          api.get("/api/users/languages"),
        ]);
        setDecks(Array.isArray(dRes.data) ? dRes.data : []);
        // Merge card types: prefer user-language translation, fall back to English
        const userCardTypes = {};
        (tRes.data || []).forEach(t => { userCardTypes[t.type_id] = t; });
        const enCardTypes = {};
        (tEnRes.data || []).forEach(t => { enCardTypes[t.type_id] = t; });
        const mergedCardTypes = [];
        const allTypeIds = new Set([...Object.keys(userCardTypes), ...Object.keys(enCardTypes)]);
        allTypeIds.forEach(id => {
          const ut = userCardTypes[id];
          const et = enCardTypes[id];
          if (ut) {
            mergedCardTypes.push({ ...ut, name: ut.name || et?.name || `Type ${id}` });
          } else if (et) {
            mergedCardTypes.push(et);
          }
        });
        setCardTypes(mergedCardTypes);

        // Merge symbols: prefer user-language translation, fall back to English
        const userSymbols = {};
        (sRes.data || []).forEach(s => { userSymbols[s.symbol_id] = s; });
        const enSymbols = {};
        (enSRes.data || []).forEach(s => { enSymbols[s.symbol_id] = s; });
        const merged = [];
        const allIds = new Set([...Object.keys(userSymbols), ...Object.keys(enSymbols)]);
        allIds.forEach(id => {
          const us = userSymbols[id];
          const es = enSymbols[id];
          if (us) {
            merged.push({ ...us, symbol_name: us.symbol_name || es?.symbol_name || `Symbol ${id}` });
          } else if (es) {
            merged.push(es);
          }
        });
        setSymbols(merged);

        setLanguages(Array.isArray(lRes.data) ? lRes.data : []);
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to load data");
      }
      setLoading(false);
    };
    fetchAll();
  }, [user]);

  useEffect(() => {
    if (!selectedDeck || selectedDeck.deck_id == null) { setDeckCards([]); return; }
    const fetchCards = async () => {
      try {
        const res = await api.get(`/api/decks/${selectedDeck.deck_id}`);
        setDeckCards(Array.isArray(res.data?.cards) ? res.data.cards : []);
      } catch { setDeckCards([]); }
    };
    fetchCards();
  }, [selectedDeck]);

  // ── Deck handlers ──

  const handleCreateDeck = async deckData => {
    try {
      const res = await api.post("/api/decks", deckData);
      const newDeckId = res.data.deckId || res.data.deck_id;
      await refreshDecks();
      try {
        const dRes = await api.get(`/api/decks/${newDeckId}`);
        setSelectedDeck(dRes.data);
        setDeckCards(Array.isArray(dRes.data?.cards) ? dRes.data.cards : []);
      } catch {
        setSelectedDeck({ deck_id: newDeckId, is_extension: false, is_public: false });
        setDeckCards([]);
      }
      setFormMode(null);
      setEditDeckData(null);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to create deck");
    }
  };

  const handleUpdateDeck = async deckData => {
    try {
      await api.put(`/api/decks/${editDeckData.deck_id}`, deckData);
      await refreshDecks();
      try {
        const res = await api.get(`/api/decks/${editDeckData.deck_id}`);
        setSelectedDeck(res.data);
        setDeckCards(Array.isArray(res.data?.cards) ? res.data.cards : []);
      } catch { /* keep existing */ }
      setFormMode(null);
      setEditDeckData(null);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update deck");
    }
  };

  const handleDeleteDeck = async deckId => {
    if (!window.confirm("Delete this deck and all its cards? This cannot be undone.")) return;
    try {
      await api.delete(`/api/decks/${deckId}`);
      if (selectedDeck?.deck_id === deckId) {
        setSelectedDeck(null);
        setDeckCards([]);
      }
      // Remove the count entry for this deck
      await refreshDecks();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to delete deck");
    }
  };

  // ── Card handlers ──

  const handleCreateCard = async cardData => {
    try {
      const targetDeckId = cardData.deckId || selectedDeck?.deck_id;
      await api.post("/api/cards", {
        deckId: targetDeckId,
        typeId: cardData.typeId,
        customBackground: cardData.customBackground,
        texts: cardData.texts,
        symbols: cardData.symbols,
        created_by: user?.user_id,
      });
      // Refresh deck list counts + selected deck's card list
      await Promise.all([refreshDecks(), fetchDeckCards()]);
      setFormMode(null);
      setEditCardData(null);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to create card");
    }
  };

  const handleUpdateCard = async cardData => {
    try {
      const oldDeckId = editCardData.deck_id;
      const newDeckId = cardData.deckId;
      await api.put(`/api/cards/${editCardData.card_id}`, {
        deckId: newDeckId,
        typeId: cardData.typeId,
        customBackground: cardData.customBackground,
        texts: cardData.texts,
      });
      // Update counts if the card was moved to a different deck -- no-op now that
      // counts come from the refreshed decks list; just refresh card list.
      await Promise.all([refreshDecks(), fetchDeckCards()]);
      setFormMode(null);
      setEditCardData(null);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update card");
    }
  };

  const handleDeleteCard = async (cardId, deckId) => {
    if (!window.confirm("Delete this card? This cannot be undone.")) return;
    try {
      await api.delete(`/api/cards/${cardId}`);
      await Promise.all([refreshDecks(), fetchDeckCards()]);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to delete card");
    }
  };

  // ── Helpers ──

  const refreshDecks = async () => {
    try {
      const res = await api.get(`/api/decks/user?lang=${encodeURIComponent(userLang)}`);
      setDecks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to refresh decks");
    }
  };

  const fetchDeckCards = async () => {
    if (!selectedDeck) { setDeckCards([]); return; }
    try {
      const res = await api.get(`/api/decks/${selectedDeck.deck_id}`);
      const cards = Array.isArray(res.data?.cards) ? res.data.cards : [];
      setDeckCards(cards);
    } catch { setDeckCards([]); }
  };

  const transformCardForEdit = useCallback(card => {
    const textsByOption = {};
    (card.texts || []).forEach(t => {
      if (!textsByOption[t.option_index]) textsByOption[t.option_index] = {};
      textsByOption[t.option_index][t.language] = t.text;
    });
    return {
      ...card,
      texts: Object.entries(textsByOption).map(([oi, trans]) => ({
        option_index: parseInt(oi, 10),
        translations: trans,
      })),
    };
  }, []);

  const getCardText = useCallback((card, lang) => {
    const t = (card.texts || []).find(t => t.option_index === 1 && t.language === lang);
    return t ? t.text : null;
  }, []);

  const getTypeSymbol = useCallback(card => {
    const ct = cardTypes.find(t => t.type_id === card.type_id);
    return ct?.symbol_filename || null;
  }, [cardTypes]);

  const handleBackgroundUpload = async file => {
    return uploadBackgroundBase64(file, "/api/cards/upload-background-base64");
  };

  const isLoading = authLoading || loading;

  // Filter symbols usable in card forms
  const cardFormSymbols = (symbols || []).filter(s => s.symbol_type === "Card type" || s.symbol_type === "Symbol");

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: C.bg, overflow: "hidden" }}>
      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "16px 24px", borderBottom: `1px solid ${C.border}`, background: C.panel,
        flexShrink: 0,
      }}>
        <h1 style={{ margin: 0, fontSize: 20, color: C.text }}>My Decks</h1>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            disabled={formMode !== null}
            onClick={() => setFormMode("deck-add")}
            style={{ padding: "8px 16px", fontSize: 14, fontWeight: 600, background: C.green, color: "#fff", border: "none", borderRadius: 6, cursor: formMode !== null ? "not-allowed" : "pointer", opacity: formMode !== null ? 0.5 : 1 }}
          >
            + New Deck
          </button>
          <button
            disabled={!selectedDeck || formMode !== null}
            onClick={() => { if (!formMode) setFormMode("card-add"); }}
            style={{ padding: "8px 16px", fontSize: 14, fontWeight: 600, background: C.yellow, color: "#1a1a1a", border: "none", borderRadius: 6, cursor: (!selectedDeck || formMode !== null) ? "not-allowed" : "pointer", opacity: (!selectedDeck || formMode !== null) ? 0.5 : 1 }}
          >
            + New Card
          </button>
        </div>
      </div>

      <Alert type="error" message={error} />

      {isLoading ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: C.textDim }}>
          Loading...
        </div>
      ) : !user ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}><span /></div>
      ) : (
        <div style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden" }}>
          {/* ── LEFT: Decks ── */}
          <div style={{ width: "33.33%", minWidth: 0, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", background: C.panel }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 15, color: C.text }}>Your Decks ({decks.length})</h2>
              <input
                type="text"
                placeholder="Search decks..."
                value={deckSearch}
                onChange={e => setDeckSearch(e.target.value)}
                style={{ padding: "4px 8px", fontSize: 13, width: 120, background: C.inputBg, border: `1px solid ${C.inputBorder}`, borderRadius: 4, color: C.text }}
              />
              <button
                disabled={formMode !== null}
                onClick={() => { setFormMode("deck-add"); setEditDeckData(null); }}
                style={{ padding: "6px 12px", fontSize: 13, fontWeight: 600, background: C.green, color: "#fff", border: "none", borderRadius: 4, cursor: formMode !== null ? "not-allowed" : "pointer", opacity: formMode !== null ? 0.5 : 1 }}
              >
                + New Deck
              </button>
            </div>
            <div style={{ flex: 1, overflowY: "auto" }}>
              {decks.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: C.textMuted }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📦</div>
                  <div style={{ fontSize: 14, color: C.textDim }}>No decks yet</div>
                  <div style={{ fontSize: 12, marginTop: 4, color: C.textMuted }}>Click "+ New Deck" to create one</div>
                </div>
              ) : (
                decks
                  .filter(deck => {
                    if (!deckSearch) return true;
                    const q = deckSearch.toLowerCase();
                    const name = deck.name || (deck.translations && Object.values(deck.translations)[0]?.name) || "";
                    return name.toLowerCase().includes(q);
                  })
                  .sort((a, b) => {
                    const nameA = a.name || (a.translations && Object.values(a.translations)[0]?.name) || `Deck ${a.deck_id}`;
                    const nameB = b.name || (b.translations && Object.values(b.translations)[0]?.name) || `Deck ${b.deck_id}`;
                    return nameA.localeCompare(nameB);
                  })
                  .map((deck, idx) => {
                  const isSelected = selectedDeck?.deck_id === deck.deck_id;
                  const deckName = deck.name ||
                    (deck.translations && Object.values(deck.translations)[0]?.name) ||
                    `Deck ${deck.deck_id}`;
                  const deckSymPath = deck.deck_symbol_path ||
                    (deck.deck_symbol_id ? symbols.find(s => s.symbol_id === deck.deck_symbol_id)?.symbol : null) ||
                    "/uploads/images/symbols/default.png";
                  return (
                    <div
                      key={deck.deck_id}
                      onClick={() => { if (!formMode) { setSelectedDeck(deck); setEditCardData(null); } }}
                      style={{
                        display: "flex", alignItems: "center", gap: 10,
                        padding: "10px 16px", cursor: formMode !== null ? "default" :"pointer",
                        borderBottom: `1px solid ${C.border}`,
                        background: isSelected ? C.panelAlt : (idx % 2 === 0 ? C.rowEven : C.rowOdd),
                        borderLeft: isSelected ? `3px solid ${C.accent}` : "3px solid transparent",
                        transition: "background 0.15s",
                        opacity: formMode !== null ? 0.5 : 1,
                      }}
                      onMouseEnter={e => { if (!isSelected && deckFormMode === null) e.currentTarget.style.background = C.hover; }}
                      onMouseLeave={e => { if (!isSelected && deckFormMode === null) e.currentTarget.style.background = idx % 2 === 0 ? C.rowEven : C.rowOdd; }}
                    >
                      <img src={deckSymPath} alt="" style={{ width: 32, height: 32, objectFit: "contain", flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500, fontSize: 14, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {deckName}
                        </div>
                        <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                          {deck.deck_id != null ? deck.card_count ?? 0 : 0} cards {deck.is_extension ? "· Extension" : ""}
                        </div>
              </div>
                      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                        <button
                          disabled={formMode !== null}
                          onClick={e => { e.stopPropagation(); if (!formMode) { setFormMode("deck-edit"); setEditDeckData(deck); } }}
                          style={{ padding: "4px 10px", fontSize: 12, fontWeight: 500, background: C.accent, color: "#fff", border: "none", borderRadius: 4, cursor: formMode !== null ? "not-allowed" : "pointer", opacity: formMode !== null ? 0.5 : 1 }}
                        >
                          Edit
                        </button>
                        <button
                          disabled={formMode !== null}
                          onClick={e => { e.stopPropagation(); if (!formMode) { handleDeleteDeck(deck.deck_id); } }}
                          style={{ padding: "4px 10px", fontSize: 12, fontWeight: 500, background: C.red, color: "#fff", border: "none", borderRadius: 4, cursor: formMode !== null ? "not-allowed" : "pointer", opacity: formMode !== null ? 0.5 : 1 }}
                        >
                          Del
                        </button>
              </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ── MIDDLE: Cards ── */}
          <div style={{ flex: 1, minWidth: 0, borderRight: `1px solid ${C.border}`, display: "flex", flexDirection: "column", background: C.bg }}>
            {selectedDeck ? (
              <>
                <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, background: C.panel }}>
                  <h2 style={{ margin: 0, fontSize: 15, color: C.text }}>
                    {selectedDeck.name || `Deck ${selectedDeck.deck_id}`}
                  </h2>
                  <input
                    type="text"
                    placeholder="Search cards..."
                    value={cardSearch}
                    onChange={e => setCardSearch(e.target.value)}
                    style={{ padding: "4px 8px", fontSize: 13, width: 120, background: C.inputBg, border: `1px solid ${C.inputBorder}`, borderRadius: 4, color: C.text }}
                  />
                  <button
                    disabled={formMode !== null}
                    onClick={() => { if (!formMode) { setFormMode("card-add"); setEditCardData(null); } }}
                    style={{ padding: "6px 14px", fontSize: 13, fontWeight: 600, background: C.yellow, color: "#1a1a1a", border: "none", borderRadius: 4, cursor: (formMode !== null) ? "not-allowed" : "pointer", opacity: (formMode !== null) ? 0.5 : 1 }}
                  >
                    + New Card
                  </button>
                </div>
                <div style={{ flex: 1, overflowY: "auto" }}>
                  {deckCards.length === 0 ? (
                    <div style={{ padding: 40, textAlign: "center", color: C.textMuted }}>
                      <div style={{ fontSize: 32, marginBottom: 8 }}>🃏</div>
                      <div style={{ fontSize: 14, color: C.textDim }}>No cards yet</div>
                      <div style={{ fontSize: 12, marginTop: 4, color: C.textMuted }}>Click "+ New Card" to add your first card</div>
                    </div>
                  ) : (
                    deckCards
                      .filter(card => {
                        if (!cardSearch) return true;
                        const q = cardSearch.toLowerCase();
                        const text = getCardText(card, userLang) || getCardText(card, "en") || "";
                        return text.toLowerCase().includes(q);
                      })
                      .sort((a, b) => {
                        const textA = getCardText(a, userLang) || getCardText(a, "en") || `Card ${a.card_id}`;
                        const textB = getCardText(b, userLang) || getCardText(b, "en") || `Card ${b.card_id}`;
                        return textA.localeCompare(textB);
                      })
                      .map((card, idx) => {
                      const firstText = getCardText(card, userLang) || getCardText(card, "en");
                      const typeSymbol = getTypeSymbol(card);
                      return (
                        <div
                          key={card.card_id}
                          onClick={() => { if (!formMode) { setFormMode("card-edit"); setEditCardData(transformCardForEdit(card)); } }}
                          style={{
                            display: "flex", alignItems: "center", gap: 10,
                            padding: "10px 16px", cursor: (formMode !== null) ? "default" : "pointer",
                            borderBottom: `1px solid ${C.border}`,
                            background: idx % 2 === 0 ? C.rowEven : C.rowOdd,
                            transition: "background 0.15s",
                            opacity: (formMode !== null) ? 0.5 : 1,
                          }}
                          onMouseEnter={e => { if (!formMode) e.currentTarget.style.background = C.hover; }}
                          onMouseLeave={e => { e.currentTarget.style.background = idx % 2 === 0 ? C.rowEven : C.rowOdd; }}
                        >
                          {/* Type symbol (like deck icon in Decks table) */}
                          <img src={typeSymbol || "/uploads/images/symbols/default.png"} alt="" style={{ width: 32, height: 32, objectFit: "contain", flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 500, fontSize: 14, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {card.type_name || `Type ${card.type_id}`}
                            </div>
                            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {firstText || "No text in your language"}
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                            <button
                              disabled={formMode !== null}
                              onClick={e => { e.stopPropagation(); if (!formMode) { setFormMode("card-edit"); setEditCardData(transformCardForEdit(card)); } }}
                              style={{ padding: "4px 10px", fontSize: 12, fontWeight: 500, background: C.accent, color: "#fff", border: "none", borderRadius: 4, cursor: (formMode !== null) ? "not-allowed" : "pointer", opacity: (formMode !== null) ? 0.5 : 1 }}
                            >
                              Edit
                            </button>
                            <button
                              disabled={formMode !== null}
                              onClick={e => { e.stopPropagation(); if (!formMode) { handleDeleteCard(card.card_id, card.deck_id); } }}
                              style={{ padding: "4px 10px", fontSize: 12, fontWeight: 500, background: C.red, color: "#fff", border: "none", borderRadius: 4, cursor: (formMode !== null) ? "not-allowed" : "pointer", opacity: (formMode !== null) ? 0.5 : 1 }}
                            >
                              Del
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            ) : (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: C.textMuted }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>👈</div>
                  <div style={{ fontSize: 14, color: C.textDim }}>Select a deck to view its cards</div>
                </div>
              </div>
            )}
          </div>

          {/* ── RIGHT: Forms ── */}
          <div style={{ width: "33.33%", minWidth: 0, display: "flex", flexDirection: "column", background: C.panel, flex: 1, minHeight: 0, overflow: "hidden" }}>
            {formMode && formMode.startsWith("deck") && (
              <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <div style={{ padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: C.panelAlt }}>
                  <h3 style={{ margin: 0, fontSize: 15, color: C.text }}>
                    {formMode === "deck-add" ? "Add New Deck" : "Edit Deck"}
                  </h3>
                  <button
                    onClick={() => { setFormMode(null); setEditDeckData(null); }}
                    style={{ padding: "4px 8px", fontSize: 12, background: "transparent", color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 4, cursor: "pointer" }}
                  >
                    ✕
                  </button>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <UserDeckForm
                    mode={formMode === "deck-edit" ? "edit" : "add"}
                    deck={editDeckData}
                    onSave={formMode === "deck-add" ? handleCreateDeck : handleUpdateDeck}
                    onCancel={() => { setFormMode(null); setEditDeckData(null); }}
                    languages={languages}
                    userDecks={decks}
                    symbols={symbols}
                  />
                </div>
              </div>
            )}

            {formMode && formMode.startsWith("card") && (
              <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                <div style={{ flexShrink: 0, padding: "12px 16px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: C.panelAlt }}>
                  <h3 style={{ margin: 0, fontSize: 15, color: C.text }}>
                    {formMode === "card-add" ? "Add Card" : "Edit Card"}
                  </h3>
                  <button
                    onClick={() => { setFormMode(null); setEditCardData(null); }}
                    style={{ padding: "4px 8px", fontSize: 12, background: "transparent", color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 4, cursor: "pointer" }}
                  >
                    ✕
                  </button>
                </div>
                <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                  {formMode === "card-add" ? (
                    <AddCardFormLocal
                      decks={decks}
                      cardTypes={cardTypes}
                      onAdd={handleCreateCard}
                      onCancel={() => { setFormMode(null); setEditCardData(null); }}
                      languages={languages}
                      symbols={cardFormSymbols}
                      backgroundUploadHandler={handleBackgroundUpload}
                      preselectedDeckId={selectedDeck.deck_id}
                    />
                  ) : editCardData ? (
                    <EditCardFormLocal
                      card={editCardData}
                      decks={decks}
                      cardTypes={cardTypes}
                      onSave={handleUpdateCard}
                      onCancel={() => { setFormMode(null); setEditCardData(null); }}
                      languages={languages}
                      symbols={cardFormSymbols}
                      backgroundUploadHandler={handleBackgroundUpload}
                      preselectedDeckId={selectedDeck?.deck_id}
                    />
                  ) : null}
                </div>
              </div>
            )}

            {!deckFormMode && !cardFormMode && (
              <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: C.textMuted, padding: 24 }}>
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>⚡</div>
                  <div style={{ fontSize: 14, color: C.textDim }}>Select a deck or create a new one</div>
                  <div style={{ fontSize: 12, marginTop: 4, color: C.textMuted }}>Use the buttons above</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MyDecks;
